/**
 * SFrame111 – Initial Data Gathering ("Let's start small.")
 * Figma Frame 11 (node 1018:13736)
 *
 * Shows header with estate name + completion %, executor badge,
 * left sidebar doc checklist, 4 horizontal category cards (Assets, Accounts,
 * Contacts, Documents), resource section, and Continue to Dashboard button.
 */

import { useNavigate, useLocation } from 'react-router-dom';
import ExecutorBadge from '../settlement/ExecutorBadge';
import videoPlayButton from '../../assets/images/video-play-button.svg';
import resourceThumbnail from '../../assets/images/resource-thumbnail.png';

interface LocationState {
  deceasedName?: string;
  estateId?: string;
}

const categories = [
  { id: 'assets', title: 'Assets', progress: 0 },
  { id: 'accounts', title: 'Accounts', progress: 0 },
  { id: 'contacts', title: 'Contacts', progress: 0 },
  { id: 'documents', title: 'Documents', progress: 0 },
];

const resources = [
  { id: 'what-is-estate', title: 'What is an estate', time: '2 min', type: 'video' as const },
  { id: 'first-3-days', title: 'The first 3 days', time: '2 min', type: 'video' as const },
  {
    id: 'probate',
    title: 'Probate explained',
    time: '10 minute read',
    type: 'article' as const,
  },
];

function TrackerRow({
  label,
  complete,
  emphasize,
  onAction,
  actionLabel,
}: {
  label: string;
  complete: boolean;
  emphasize?: boolean;
  onAction?: () => void;
  actionLabel?: string;
}) {
  const inner = (
    <>
      <span
        className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 ${
          complete ? 'border-cyan-600 bg-cyan-600' : 'border-stone-300 bg-white'
        }`}
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <p className={`text-sm font-medium leading-snug ${emphasize ? 'text-stone-900' : 'text-stone-700'}`}>
          {label}
        </p>
        {actionLabel && onAction && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onAction();
            }}
            className="mt-1 text-left text-xs font-semibold text-cyan-700 underline decoration-cyan-600/40 underline-offset-2 hover:text-cyan-800"
          >
            {actionLabel}
          </button>
        )}
      </div>
    </>
  );

  if (onAction && !actionLabel) {
    return (
      <button
        type="button"
        onClick={onAction}
        className="flex w-full items-start gap-3 rounded-lg p-1 text-left transition-colors hover:bg-stone-100/80"
      >
        {inner}
      </button>
    );
  }

  return <div className="flex items-start gap-3 rounded-lg p-1">{inner}</div>;
}

export default function SFrame111() {
  const navigate = useNavigate();
  const location = useLocation();
  const { deceasedName, estateId } = (location.state as LocationState) || {};

  const displayName = deceasedName || 'Your Loved One';

  const goToDeathCertUpload = () => {
    if (estateId) {
      navigate(`/sframe-124/${estateId}`);
      return;
    }
    navigate('/sframe-124');
  };

  const goToDashboard = () => {
    if (estateId) {
      navigate(`/sframe-117/${estateId}`);
    } else {
      navigate('/sframe-117');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Top header bar */}
      <div className="bg-dark-gradient py-3 px-6 flex items-center justify-between">
        <div />
        <div className="text-center">
          <p className="text-white-alt font-semibold text-base tracking-wide font-display">
            {displayName}'s Estate Settlement
          </p>
          <p className="text-green-300 text-xs mt-0.5">5% complete</p>
        </div>
        <ExecutorBadge estateName={displayName} />
      </div>

      {/* ── 1. Hero / intro (white) ── */}
      <div className="max-w-2xl mx-auto px-4 pt-8 pb-8 w-full">
        <div className="text-center mb-4">
          <h1 className="font-display text-2xl sm:text-3xl text-stone-800 mb-3">Let&apos;s start small.</h1>
          <p className="text-sm text-stone-600 max-w-lg mx-auto leading-relaxed mb-4">
            Input all the information you currently have. Don&apos;t worry about not having everything, we will guide
            you through the rest.
          </p>
          <p className="text-xs text-stone-500 mb-6">
            *To protect this estate and begin closing accounts, a certified Death Certificate is required to unlock
            settlement features*
          </p>
          <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <button
              type="button"
              onClick={goToDeathCertUpload}
              className="inline-flex items-center justify-center rounded-lg bg-cyan-700 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-cyan-800"
            >
              Upload files — death certificate
            </button>
            <button
              type="button"
              onClick={goToDeathCertUpload}
              className="text-sm font-medium text-cyan-700 underline underline-offset-2 hover:text-cyan-800"
            >
              Open death certificate checklist
            </button>
          </div>
        </div>
      </div>

      {/* ── 2. Task modules (stone-50 + curve) ── */}
      <div className="flex-1 rounded-t-3xl bg-stone-50 border-t border-stone-100 shadow-[0_-4px_24px_rgba(0,0,0,0.04)]">
        <div className="max-w-2xl mx-auto px-4 py-8 w-full">
          <div className="flex flex-col gap-8 md:flex-row md:items-start">
            {/* Left progress tracker */}
            <div className="w-full shrink-0 md:w-48">
              <p className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-4">Progress</p>
              <div className="flex flex-col gap-3">
                <TrackerRow
                  label="Death Certificate*"
                  complete={false}
                  emphasize
                  onAction={goToDeathCertUpload}
                  actionLabel="Upload files"
                />
                <TrackerRow label="Last Will & Testament" complete />
                <TrackerRow label="Letters Testamentary" complete={false} />
              </div>
            </div>

            {/* Category cards */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-stone-800 mb-4">Gather details</p>
              <div className="grid grid-cols-2 gap-4">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    className="cursor-pointer rounded-xl border border-stone-200 bg-white p-5 text-left shadow-sm transition hover:border-cyan-500 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2"
                  >
                    <span className="font-semibold text-sm text-stone-800 block mb-2">{cat.title}</span>
                    <div className="flex items-center gap-2 text-stone-600">
                      <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <circle cx="12" cy="12" r="10" strokeWidth={2} />
                      </svg>
                      <span className="text-xs font-medium">{cat.progress}%</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── 3. Bottom: resources (white band) + boxed resources + CTA ── */}
      <div className="bg-white max-w-2xl mx-auto px-4 py-8 mb-8 w-full">
        <div className="bg-stone-50 rounded-xl p-6 border border-stone-100">
          <h2 className="text-center text-sm font-semibold text-stone-800 mb-2">Educational resources</h2>
          <p className="text-sm text-stone-600 text-center mb-4">
            Not ready to dive in? Learn more about estate settlement with these guides.
          </p>
          <ul className="mb-6 space-y-2 text-center text-sm">
            <li>
              <button type="button" className="font-medium text-cyan-700 hover:text-cyan-800">
                Estate settlement overview
              </button>
            </li>
            <li>
              <button type="button" className="font-medium text-cyan-700 hover:text-cyan-800">
                What to do in the first weeks
              </button>
            </li>
            <li>
              <button type="button" className="font-medium text-cyan-700 hover:text-cyan-800">
                Working with banks and institutions
              </button>
            </li>
          </ul>

          <div className="flex gap-4 overflow-x-auto pb-2 -mx-2 px-2">
            {resources.map((res) => (
              <div key={res.id} className="flex-shrink-0 w-[140px] cursor-pointer group">
                <div className="relative w-full h-[90px] rounded-xl overflow-hidden mb-2 shadow-md border border-stone-200">
                  {res.type === 'video' ? (
                    <>
                      <img src={resourceThumbnail} alt="" className="w-full h-full object-cover bg-stone-800" />
                      <div className="absolute inset-0 bg-stone-900/75 flex items-center justify-center">
                        <img src={videoPlayButton} alt="Play" className="w-8 h-8 brightness-0 invert" />
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-full bg-stone-700 flex items-center justify-center">
                      <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M4 6h16M4 10h16M4 14h10M4 18h10"
                        />
                      </svg>
                    </div>
                  )}
                </div>
                <p className="text-xs font-medium text-stone-800 group-hover:text-cyan-700 transition-colors">
                  {res.title}
                </p>
                <p className="text-[10px] text-stone-500">{res.time}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={goToDashboard}
            className="bg-cyan-700 text-white px-6 py-3 rounded-lg font-semibold shadow-sm transition hover:bg-cyan-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2"
          >
            Continue to dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
